import React, { memo, ReactNode } from 'react';

import { IconShine } from 'src/components/Icons/ShineIcon';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { Enums } from 'src/models/enums';
import { ItemCategoryType, MenuItemEnum } from 'src/models/Partner/Sidebar';
import { SidebarMenuItem } from 'src/components/Sidebar/MenuItem';
import { showPlusSubscriptionModal } from 'src/store/plusSubscription/helpers';
import { useBemHelper } from 'src/utils/hooks/useBemHelper';
import { MenuStartGuideIcon } from 'src/components/Icons/Menu/StartGuide';
import { MenuExperiencesIcon } from 'src/components/Icons/Menu/Experiences';
import { MenuSupportersIcon } from 'src/components/Icons/Menu/Supporters';
import { MenuEmailMarketingIcon } from 'src/components/Icons/Menu/EmailMarketing';
import { MenuReportsIcon } from 'src/components/Icons/Menu/Reports';
import { MenuHelpCenterIcon } from 'src/components/Icons/Menu/HelpCenter';
import { MenuSettingsIcon } from 'src/components/Icons/Menu/Settings';
import { MenuAdministrationIcon } from 'src/components/Icons/Menu/Administration';
import { MenuSwitchOrganizationIcon } from 'src/components/Icons/Menu/SwitchOrganization';
import { IconChevronRight } from 'src/components/Icons/ChevronRight';
import { UiButton } from 'src/components/UI/Button';

type CategoryProps = {
  category: ItemCategoryType;
  changeCurrentOrg: (a: string) => void;
  selectedItemId: number | null;
  toggleOrganizationsList: () => void;
  disabled: boolean;
  organizationsExist: boolean;
  isCurrentPageSPA: boolean;
};

export const SidebarItemCategory = memo<CategoryProps>(
  ({ category, selectedItemId, toggleOrganizationsList, disabled, organizationsExist, isCurrentPageSPA }) => {
    const [bemHelper, bemHelperItem] = useBemHelper(['sidebar', 'sidebar-item']);

    const getIconItem = (id: number): ReactNode => {
      let Component = null;

      switch (id) {
        case MenuItemEnum.StartGuide:
          Component = MenuStartGuideIcon;
          break;
        case MenuItemEnum.MyCampaigns:
          Component = MenuExperiencesIcon;
          break;
        case MenuItemEnum.Supporters:
          Component = MenuSupportersIcon;
          break;
        case MenuItemEnum.EmailMarketing:
          Component = MenuEmailMarketingIcon;
          break;
        case MenuItemEnum.HelpCenter:
          Component = MenuHelpCenterIcon;
          break;
        case MenuItemEnum.Administration:
        case MenuItemEnum.PartnerAdministration:
          Component = MenuAdministrationIcon;
          break;
        case MenuItemEnum.SwitchAccount:
          Component = MenuSwitchOrganizationIcon;
          break;
        case MenuItemEnum.PledgeFunding:
          Component = MenuAdministrationIcon;
          break;
        case MenuItemEnum.Settings:
          Component = MenuSettingsIcon;
          break;
        case MenuItemEnum.OrganizationReports:
          Component = MenuReportsIcon;
          break;
        default:
          return null;
      }

      return <Component className="sidebar-item__icon" />;
    };

    const handleClick = () => showPlusSubscriptionModal('upgradeNow', { canClose: true });

    return (
      <>
        {category.id === 2 && <hr className={bemHelper('divider')} />}
        {category.id === 3 && <div className={bemHelper('filler')} />}

        {category.items.map((item) => {
          if (item.id === Enums.user.sidebarItemTypeCode.SwitchAccount) {
            return (
              organizationsExist && (
                <UiButton
                  key={item.id}
                  className={bemHelperItem()}
                  onClick={toggleOrganizationsList}
                  dataTest="leftSideMenu"
                >
                  <div className={bemHelperItem('icon-wrapper')}>{getIconItem(item.id)}</div>
                  <span className={bemHelperItem('text')}>{item.title}</span>
                  <IconChevronRight className={bemHelperItem('icon-chevron')} />
                </UiButton>
              )
            );
          }

          if (item.id === Enums.user.sidebarItemTypeCode.SubscriptionButton) {
            return item.url ? (
              <UiInnerNotSPALink
                href={item.url}
                key={item.id}
                className={`btn ${bemHelper('icon-shine_subscription')}`}
                dataTestId={item.testId}
              >
                <IconShine />
                <span>{item.title}</span>
              </UiInnerNotSPALink>
            ) : (
              <UiButton
                key={item.id}
                className={bemHelper('icon-shine_subscription')}
                onClick={handleClick}
                dataTestId={item.testId}
              >
                <IconShine />
                <span>{item.title}</span>
              </UiButton>
            );
          }

          return (
            <SidebarMenuItem
              key={item.id}
              item={item}
              selectedItemId={selectedItemId}
              icon={getIconItem(item.id)}
              disabled={disabled}
              isCurrentPageSPA={isCurrentPageSPA}
            />
          );
        })}
      </>
    );
  }
);
